import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useReveal } from '../motion';
import { items, safeHref, safeImage, str, type StorefrontBlock } from './types';
import { ProductGridBlock } from './ProductGridBlock';
import { CategoryStripBlock } from './CategoryStripBlock';
import { ProductRowBlock } from './ProductRowBlock';
import { PromoTilesBlock } from './PromoTilesBlock';

/**
 * Vykresleni bloku slozenych v builderu.
 *
 * Kazdy blok je bezny React komponent – zadny `dangerouslySetInnerHTML`.
 * Obsluha sklada z hotovych dilku, ne z HTML, takze do stranky nema jak
 * dostat cizi kod.
 */

export function BlockList({ blocks }: { blocks: readonly StorefrontBlock[] }) {
  return (
    <>
      {blocks.map((block) => (
        <Block key={block.id} block={block} />
      ))}
    </>
  );
}

function Block({ block }: { block: StorefrontBlock }) {
  switch (block.kind) {
    case 'hero':
      return <HeroBlock config={block.config} />;
    case 'heroCarousel':
      return <HeroCarouselBlock config={block.config} />;
    case 'usp':
      return <UspBlock config={block.config} />;
    case 'categories':
      return <CategoryStripBlock config={block.config} />;
    case 'products':
      return <ProductGridBlock config={block.config} />;
    case 'productRow':
      return <ProductRowBlock config={block.config} />;
    case 'promoTiles':
      return <PromoTilesBlock config={block.config} />;
    case 'banner':
      return <BannerBlock config={block.config} />;
    case 'richtext':
      return <RichTextBlock config={block.config} />;
    default:
      // Novejsi administrace muze poslat blok, ktery tahle verze vylohy
      // nezna. Preskocit ho je lepsi nez spadnout.
      return null;
  }
}

type Config = Record<string, unknown>;

function HeroBlock({ config }: { config: Config }) {
  const { ref, className } = useReveal<HTMLElement>();
  const layout = str(config, 'layout', 'split');
  const image = safeImage(config, 'imageUrl');
  const primaryHref = safeHref(config, 'primaryHref');
  const secondaryHref = safeHref(config, 'secondaryHref');
  const primaryLabel = str(config, 'primaryLabel');
  const secondaryLabel = str(config, 'secondaryLabel');
  const title = str(config, 'title');
  const eyebrow = str(config, 'eyebrow');
  const text = str(config, 'text');

  return (
    <section ref={ref} className={`block hero hero-${layout} ${className}`}>
      <div className="hero-glow" aria-hidden="true" />
      <div className="hero-copy">
        {eyebrow !== '' && <span className="eyebrow">{eyebrow}</span>}
        {title !== '' && <h1 className="hero-title">{title}</h1>}
        {text !== '' && <p className="hero-text">{text}</p>}
        {(primaryLabel !== '' || secondaryLabel !== '') && (
          <div className="hero-actions">
            {primaryLabel !== '' && primaryHref !== null && (
              <HeroLink href={primaryHref} className="button primary large">
                {primaryLabel}
              </HeroLink>
            )}
            {secondaryLabel !== '' && secondaryHref !== null && (
              <HeroLink href={secondaryHref} className="button ghost large">
                {secondaryLabel}
              </HeroLink>
            )}
          </div>
        )}
      </div>

      {layout === 'split' && (
        <div className="hero-art" aria-hidden="true">
          {image === null ? <HeroPattern /> : <img src={image} alt="" />}
        </div>
      )}
    </section>
  );
}

/**
 * Rotující úvodní pruh. Slidy se posouvají samy (6 s), s ohledem na omezení
 * pohybu v systému. Šipky a tečky umožní ovládání ručně.
 */
function HeroCarouselBlock({ config }: { config: Config }) {
  const slides = items(config, 'slides');
  const count = slides.length;
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (count <= 1) return undefined;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return undefined;
    const timer = window.setInterval(() => setIndex((current) => (current + 1) % count), 6000);
    return () => window.clearInterval(timer);
  }, [count]);

  if (count === 0) return null;
  const go = (next: number) => setIndex(((next % count) + count) % count);

  return (
    <section className="block hero-carousel" aria-roledescription="carousel" aria-label="Akce a novinky">
      <div className="hero-carousel-viewport">
        {slides.map((slide, i) => (
          <HeroSlide key={i} slide={slide} active={i === index} />
        ))}
      </div>

      {count > 1 && (
        <>
          <button
            type="button"
            className="carousel-arrow prev"
            aria-label="Předchozí"
            onClick={() => go(index - 1)}
          >
            <span aria-hidden="true">‹</span>
          </button>
          <button
            type="button"
            className="carousel-arrow next"
            aria-label="Další"
            onClick={() => go(index + 1)}
          >
            <span aria-hidden="true">›</span>
          </button>
          <div className="carousel-dots">
            {slides.map((_, i) => (
              <button
                key={i}
                type="button"
                className={i === index ? 'dot is-active' : 'dot'}
                aria-label={`Přejít na slide ${i + 1}`}
                aria-current={i === index}
                onClick={() => go(i)}
              />
            ))}
          </div>
        </>
      )}
    </section>
  );
}

function HeroSlide({ slide, active }: { slide: Record<string, unknown>; active: boolean }) {
  const eyebrow = str(slide, 'eyebrow');
  const title = str(slide, 'title');
  const text = str(slide, 'text');
  const ctaLabel = str(slide, 'ctaLabel');
  const href = safeHref(slide, 'href');
  const image = safeImage(slide, 'imageUrl');
  const tone = str(slide, 'tone', 'accent');

  return (
    <div className={`hero-slide slide-${tone}${active ? ' is-active' : ''}`} aria-hidden={!active}>
      <div className="hero-copy">
        {eyebrow !== '' && <span className="eyebrow">{eyebrow}</span>}
        {title !== '' && <h2 className="hero-title">{title}</h2>}
        {text !== '' && <p className="hero-text">{text}</p>}
        {ctaLabel !== '' && href !== null && (
          <HeroLink href={href} className="button primary large">
            {ctaLabel}
          </HeroLink>
        )}
      </div>
      <div className="hero-art" aria-hidden="true">
        {image === null ? <HeroPattern /> : <img src={image} alt="" />}
      </div>
    </div>
  );
}

/**
 * Kotva vs. cesta.
 *
 * `#zbozi` musi zustat obycejny <a>, jinak by router zkusil prejit na
 * stranku s tim nazvem. Cesta naopak musi jit pres <Link>, aby se
 * neprenacetla cela aplikace.
 */
function HeroLink({
  href,
  className,
  children,
}: {
  href: string;
  className: string;
  children: React.ReactNode;
}) {
  if (href.startsWith('#')) {
    return (
      <a href={href} className={className}>
        {children}
      </a>
    );
  }
  return (
    <Link to={href} className={className}>
      {children}
    </Link>
  );
}

/** Zastupna grafika, dokud obsluha nenahraje vlastni obrazek. */
function HeroPattern() {
  return (
    <div className="hero-pattern">
      <span className="orb orb-1" />
      <span className="orb orb-2" />
      <span className="orb orb-3" />
      <svg viewBox="0 0 200 200" role="presentation">
        <path
          d="M100 24c22 0 34 14 34 34 0 12-6 20-6 30 0 14 18 20 18 40 0 24-20 42-46 42s-46-18-46-42c0-20 18-26 18-40 0-10-6-18-6-30 0-20 12-34 34-34Z"
          fill="currentColor"
          opacity="0.14"
        />
        <circle cx="66" cy="70" r="16" fill="currentColor" opacity="0.22" />
        <circle cx="134" cy="70" r="16" fill="currentColor" opacity="0.22" />
        <circle cx="52" cy="112" r="13" fill="currentColor" opacity="0.18" />
        <circle cx="148" cy="112" r="13" fill="currentColor" opacity="0.18" />
      </svg>
    </div>
  );
}

function UspBlock({ config }: { config: Config }) {
  const { ref, className } = useReveal<HTMLElement>();
  const list = items(config, 'items');
  if (list.length === 0) return null;

  return (
    <section ref={ref} className={`block usp ${className}`}>
      {list.map((item, index) => (
        <div key={index} className="usp-item" style={{ '--i': index } as React.CSSProperties}>
          <span className="usp-mark" aria-hidden="true">
            {index + 1}
          </span>
          <div>
            <strong>{str(item, 'title')}</strong>
            <p className="muted small">{str(item, 'text')}</p>
          </div>
        </div>
      ))}
    </section>
  );
}

function BannerBlock({ config }: { config: Config }) {
  const { ref, className } = useReveal<HTMLElement>();
  const tone = str(config, 'tone', 'accent');
  const href = safeHref(config, 'buttonHref');
  const label = str(config, 'buttonLabel');
  const title = str(config, 'title');
  const text = str(config, 'text');

  if (title === '' && text === '') return null;

  return (
    <section ref={ref} className={`block banner banner-${tone} ${className}`}>
      <div className="banner-copy">
        {title !== '' && <h2>{title}</h2>}
        {text !== '' && <p>{text}</p>}
      </div>
      {label !== '' && href !== null && (
        <HeroLink href={href} className="button banner-button">
          {label}
        </HeroLink>
      )}
    </section>
  );
}

function RichTextBlock({ config }: { config: Config }) {
  const { ref, className } = useReveal<HTMLElement>();
  const align = str(config, 'align', 'left');
  const title = str(config, 'title');
  const text = str(config, 'text');
  if (title === '' && text === '') return null;

  return (
    <section ref={ref} className={`block richtext align-${align} ${className}`}>
      {title !== '' && <h2>{title}</h2>}
      {/*
        Odstavce delime podle prazdneho radku. Zamerne zadne HTML –
        text z administrace se vykresluje jako text.
      */}
      {text
        .split(/\n{2,}/)
        .filter((paragraph) => paragraph.trim() !== '')
        .map((paragraph, index) => (
          <p key={index}>{paragraph}</p>
        ))}
    </section>
  );
}
